import { PersonIcon } from "@radix-ui/react-icons";
import {
  Building2,
  CalendarIcon,
  FileText,
  Globe,
  Newspaper,
  Rss,
  Sparkles,
  Ticket,
  Route as JourneyPortfolioIcon,
} from "lucide-react";
import {
  HomeIcon,
  MindShareSectionIcon,
  NetworkSectionIcon,
  PersonalAdvisoryIcon,
  StarsTabIcon,
  WhatAreStarsIcon,
} from "@/components/icons/icons";

export type UserRole = "advisor" | "achiever";

export interface SidebarItemConfig {
  id: string;
  label: string;
  path?: string;
  icon: React.ReactNode;
  tourId?: string;
  /** Other routes that count as this item (keep it highlighted), e.g. the
   *  XO Home item while inside the Getting Started flow. */
  activePaths?: string[];
}

/**
 * A collapsible, labelled block of nav items — the "NETWORK" heading in the
 * Figma, with its own icon, a chevron, and indented children.
 *
 * A group rather than more flat items because the design gives the heading its
 * own treatment (uppercase, orange, its own mark) and indents what sits under
 * it, which is a different thing from a nav item that happens to be styled
 * differently.
 */
export interface SidebarGroupConfig {
  id: string;
  label: string;
  icon: React.ReactNode;
  /**
   * Tailwind text colour for the heading — its mark and label share it.
   *
   * Per group rather than one nav-wide value: NETWORK and MindShare are
   * deliberately told apart by colour as well as by glyph, so the accent
   * belongs to the group that owns it.
   */
  accentClassName: string;
  /** Open on first render. Collapsed state is remembered per group thereafter. */
  defaultOpen?: boolean;
  items: SidebarItemConfig[];
}

/**
 * Returns the filtered menu items for the sidebar.
 * Advisors see PABs once their application is approved -- pre-approval they
 * have no PAB access yet, so it's hidden (matches the Figma FTUX-state nav).
 * For achievers: the PAB item is hidden once they have an Expert Program.
 *
 * "Programs" is NOT conditional -- it is always shown. It used to be hidden
 * until the achiever had a program, which made the one nav item that leads to
 * "you have no programs yet, here's how to get one" impossible to reach exactly
 * when it was most useful. An empty Programs page is a better answer than a
 * missing one, and AchieverProgramPage already renders that empty state.
 *
 * "Explore" is a permanent nav item for BOTH roles (browse/join curated
 * programs), shown alongside "Home" regardless of program state -- it used to
 * replace "Home" until the user had a program, but Explore is useful on an
 * ongoing basis (e.g. joining a second Expert Program), not just pre-signup.
 * It was previously achiever-only, which meant a dual-role user switched into
 * the "Expert" workspace (WorkspaceToggle) lost access to it entirely, even
 * though browsing/joining programs isn't achiever-specific. The "Build a
 * PAB" card *inside* Explore is what conditions on hasPab/workspace now (see
 * ExplorePage.tsx), not the nav item itself.
 */
export const getMenuItems = (
  role: UserRole,
  hasExpert: boolean,
  hasPab?: boolean,
  advisorApproved = true,
  hasPeerApplications = false,
) => {
  const items = sidebarConfig[role].menu;
  if (role === "advisor") {
    // "Applications" is shown only when there is something in it. A facilitator
    // with no groups (or none open) has an inbox that can only ever be empty,
    // and a permanent nav item leading to an empty page is noise -- the page is
    // still reachable by URL and from each program card.
    const withApplications = hasPeerApplications
      ? items
      : items.filter((item) => item.id !== "peer-applications");
    return advisorApproved
      ? withApplications
      : withApplications.filter((item) => item.id !== "pab");
  }

  // Achiever. Programs is unconditional (see the docstring); only PAB is gated,
  // and only on already having an Expert Program.
  return hasExpert && !hasPab ? items.filter((item) => item.id !== "pab") : items;
};

/**
 * The XO-facing NETWORK block (Figma 3:1357).
 *
 * These destinations did not exist before this design — they resolve to
 * placeholder pages (see App.tsx) rather than 404ing, so a nav item leads
 * somewhere that explains itself while the real pages are built.
 */
const NETWORK_GROUP: SidebarGroupConfig = {
  id: "network",
  label: "Network",
  icon: <NetworkSectionIcon />,
  accentClassName: "text-theme-nav-section-network",
  defaultOpen: true,
  items: [
    {
      id: "mindshare",
      label: "Feed",
      path: "/mindshare",
      icon: <Newspaper className="w-5 h-5" />,
      tourId: "nav-mindshare",
    },
    {
      id: "directory",
      label: "Directory",
      path: "/directory",
      icon: <Globe className="w-5 h-5" />,
      tourId: "nav-directory",
    },
    {
      id: "events",
      label: "Events",
      path: "/events",
      icon: <Ticket className="w-5 h-5" />,
      tourId: "nav-events",
    },
  ],
};

/**
 * The achiever-facing MindShare block (Figma 3:1272 / 3:1274).
 *
 * Its own mark and its own crimson accent — NOT the NETWORK treatment. The two
 * headings are distinguished by colour and glyph together, which is why the
 * accent lives on the group rather than on the nav.
 *
 * `Events` is the same destination the XO group points at; both roles see the
 * same events, so it is one route rather than two.
 *
 * `Feed` is XO's Mindshare on its own route, `/feed` — the same MindSharePage
 * with `readOnly`, which removes creation and nothing else.
 */
const MINDSHARE_GROUP: SidebarGroupConfig = {
  id: "mindshare-group",
  label: "MindShare",
  icon: <MindShareSectionIcon />,
  accentClassName: "text-theme-nav-section-mindshare",
  defaultOpen: true,
  items: [
    {
      id: "feed",
      label: "Feed",
      path: "/feed",
      icon: <Rss className="w-5 h-5" />,
      tourId: "nav-feed",
    },
    {
      id: "events",
      label: "Events",
      path: "/events",
      icon: <Ticket className="w-5 h-5" />,
      tourId: "nav-events",
    },
  ],
};

export const getMenuGroups = (role: UserRole): SidebarGroupConfig[] =>
  sidebarConfig[role].groups ?? [];

// SUPERSEDED: organisation programs are in the side nav again, as one item.
// // No ORGANIZATION group: organisation programs are never shown in the side
// // nav. Members reach them from Journey Portfolio and the Programs tab.

/**
 * The one "Organisation" item: opens the caller's current Organisation
 * program. Only the program -- its North Stars, OKRs and OKIs are tabs on that
 * page, not nav items (the old ORGANIZATION group listed them). Built per
 * program because the path carries its id; shown only to members of one.
 * `allExternalIds` keeps it lit on whichever of the viewer's organisations
 * the page's switcher has moved to.
 */
export const getOrganisationItem = (
  externalId: string,
  allExternalIds: string[] = [],
): SidebarItemConfig => ({
  id: "organisation",
  label: "Organization",
  path: `/organisation-programs/${externalId}`,
  icon: <Building2 className="w-5 h-5" />,
  tourId: "nav-organisation",
  activePaths: allExternalIds.map((id) => `/organisation-programs/${id}`),
});

export const sidebarConfig: Record<
  UserRole,
  {
    menu: SidebarItemConfig[];
    /** Rendered under `menu`, each as a collapsible labelled block. */
    groups?: SidebarGroupConfig[];
    bottom: SidebarItemConfig[];
  }
> = {
  achiever: {
    menu: [
      {
        id: "home",
        label: "Home",
        path: "/home",
        icon: <HomeIcon className="w-5 h-5" />,
        tourId: "nav-home",
      },
      {
        id: "explore",
        label: "Explore",
        path: "/explore",
        icon: <Sparkles className="w-5 h-5" />,
        tourId: "nav-explore",
      },
      {
        id: "journey-portfolio",
        label: "My Journey",
        path: "/journey-portfolio",
        icon: <JourneyPortfolioIcon className="w-5 h-5" />,
        tourId: "nav-journey-portfolio",
      },
      // {
      //   id: "pab",
      //   label: "PAB",
      //   path: "/pab",
      //   icon: <PersonalAdvisoryIcon className="w-5 h-5" />,
      //   tourId: "nav-pabs",
      // },
      // {
      //   id: "programs",
      //   label: "Programs",
      //   path: "/programs",
      //   icon: <WhatAreStarsIcon className="w-5 h-5" />,
      //   tourId: "nav-programs",
      // },
      // {
      //   id: "sessions",
      //   label: "Sessions",
      //   path: "/sessions",
      //   icon: <CalendarIcon className="w-5 h-5" />,
      //   tourId: "nav-sessions",
      // },
      // {
      //   id: "goals",
      //   label: "Goals",
      //   path: "/goals",
      //   icon: <TargetIcon className="w-5 h-5" />,
      //   tourId: "nav-goals",
      // },
      {
        id: "stars",
        label: "S.T.A.R.s",
        path: "/stars",
        icon: <StarsTabIcon className="w-5 h-5" />,
      },
      {
        id: "profile",
        label: "Profile",
        path: "/profile",
        icon: <PersonIcon className="w-5 h-5" />,
        tourId: "nav-profile",
      },
    ],
    groups: [MINDSHARE_GROUP],
    bottom: [],
  },

  advisor: {
    menu: [
      // Home is the Getting Started flow for now (HomePage renders it in the
      // XO workspace). The old XO dashboard -- PAB requests, active boards --
      // is hidden until the new Home redesign brings that content back.
      // /getting-started (where the dock and step links go) keeps Home lit.
      {
        id: "home",
        label: "Home",
        path: "/home",
        icon: <HomeIcon className="w-5 h-5" />,
        tourId: "nav-home",
        activePaths: ["/getting-started"],
      },
      {
        id: "pab",
        label: "Advisory Boards",
        path: "/pab",
        icon: <PersonalAdvisoryIcon className="w-5 h-5" />,
        tourId: "nav-pabs",
      },
      {
        id: "programs",
        label: "Programs",
        path: "/programs",
        icon: <WhatAreStarsIcon className="w-5 h-5" />,
        tourId: "nav-programs",
      },
      {
        id: "peer-applications",
        label: "Applications",
        path: "/peer-advisory/applications",
        icon: <FileText className="w-5 h-5" />,
        tourId: "nav-peer-applications",
      },
      {
        id: "sessions",
        label: "Sessions",
        path: "/sessions",
        icon: <CalendarIcon className="w-5 h-5" />,
        tourId: "nav-sessions",
      },
      {
        id: "profile",
        label: "Profile",
        path: "/profile",
        icon: <PersonIcon className="w-5 h-5" />,
        tourId: "nav-profile",
      },
    ],
    groups: [NETWORK_GROUP],
    bottom: [],
  },
};
